import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, X, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { IMAGE_LIBRARY } from '@/data/api';
import { api, getApiErrorMessage, resourceEndpoints } from '@/lib/api';
import { toast } from 'sonner';

const extractUploadedImage = (payload) => {
  const source = payload?.data ?? payload;
  if (typeof source === 'string') return source;
  return source?.url
    ?? source?.path
    ?? source?.image
    ?? source?.image_url
    ?? source?.file
    ?? source?.file_path
    ?? source?.filename
    ?? null;
};

export const VehicleImagePicker = ({
  open,
  onClose,
  onConfirm,
  currentImage,
  objectTypeId,
  defaultImage,
  objectTypeName,
  uploadEndpoint = resourceEndpoints.uploadImage,
}) => {
  const [tab, setTab] = useState('library');
  const [selected, setSelected] = useState(currentImage || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [filterKind, setFilterKind] = useState('all');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const defaultForType = defaultImage || null;
  const typeName = objectTypeName || 'este tipo de objeto';

  useEffect(() => {
    if (!open) return;
    setSelected(currentImage || null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }, [currentImage, open]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens sao aceites.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem acima de 5MB nao e suportada.');
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const nextPreviewUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(nextPreviewUrl);
    setSelected(nextPreviewUrl);
    setTab('upload');
  };

  const clearUpload = () => {
    setSelectedFile(null);
    setSelected(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const uploadSelectedFile = async () => {
    const formData = new FormData();
    formData.append('image', selectedFile);
    if (objectTypeId) formData.append('object_type_id', objectTypeId);

    const response = await api.post(uploadEndpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const uploadedImage = extractUploadedImage(response.data);
    if (!uploadedImage) throw new Error('A API nao devolveu o caminho da imagem.');
    return uploadedImage;
  };

  const handleConfirm = async () => {
    if (!selected) {
      toast.error('Escolha uma imagem primeiro.');
      return;
    }

    setUploading(true);
    try {
      const image = selectedFile ? await uploadSelectedFile() : selected;
      onConfirm(image);
      onClose();
      if (selectedFile) toast.success('Imagem carregada.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possivel carregar a imagem.'));
    } finally {
      setUploading(false);
    }
  };

  const libraryFiltered = filterKind === 'all' ? IMAGE_LIBRARY : IMAGE_LIBRARY.filter(image => image.kind === filterKind);
  const kinds = Array.from(new Set(IMAGE_LIBRARY.map(image => image.kind)));

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="vehicle-image-picker">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon size={18} className="text-blue-600" />
            Escolher imagem
          </DialogTitle>
          <DialogDescription>
            Selecione uma imagem de stock, carregue a sua, ou use a imagem padrao do tipo de objeto.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-slate-200 -mb-px">
          {[
            { id: 'library', label: 'Biblioteca' },
            { id: 'upload', label: 'Carregar foto' },
            { id: 'default', label: 'Padrao do tipo' },
          ].map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              data-testid={`image-tab-${item.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === item.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {tab === 'library' && (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Button
                  type="button"
                  variant={filterKind === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterKind('all')}
                  className={filterKind === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  disabled={uploading}
                >
                  Todos
                </Button>
                {kinds.map(kind => (
                  <Button
                    key={kind}
                    type="button"
                    variant={filterKind === kind ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterKind(kind)}
                    className={filterKind === kind ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    disabled={uploading}
                  >
                    {kind}
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {libraryFiltered.map(image => {
                  const active = selected === image.url;
                  return (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setSelected(image.url);
                      }}
                      disabled={uploading}
                      data-testid={`image-library-${image.id}`}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${active ? 'border-blue-600 ring-2 ring-blue-400' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <img src={image.url} alt={image.label} className="w-full aspect-square object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left">
                        <p className="text-[10px] text-white font-medium truncate">{image.label}</p>
                        <Badge variant="outline" className="text-[9px] bg-white/10 text-white border-white/30 mt-0.5">{image.kind}</Badge>
                      </div>
                      {active && (
                        <div className="absolute top-1 right-1 bg-blue-600 rounded-full p-0.5">
                          <Check size={12} className="text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {tab === 'upload' && (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              {selectedFile && previewUrl ? (
                <>
                  <img src={previewUrl} alt="Pre-visualizacao" className="max-h-64 rounded-lg border border-slate-200" />
                  <p className="text-xs text-slate-500">{selectedFile.name}</p>
                  <Button variant="outline" size="sm" onClick={clearUpload} disabled={uploading}>
                    <X size={14} className="mr-1" /> Remover
                  </Button>
                </>
              ) : (
                <div className="border-2 border-dashed border-slate-300 rounded-xl w-full py-16 flex flex-col items-center justify-center gap-3 bg-slate-50">
                  <Upload size={36} className="text-slate-400" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-700">Clique para carregar uma imagem</p>
                    <p className="text-xs text-slate-500">PNG, JPG ate 5MB</p>
                  </div>
                  <Label htmlFor="vehicle-image-upload">
                    <Input id="vehicle-image-upload" ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="image-upload-input" />
                    <Button type="button" className="bg-blue-600 hover:bg-blue-700" disabled={uploading} onClick={() => fileRef.current?.click()}>
                      Escolher ficheiro
                    </Button>
                  </Label>
                </div>
              )}
            </div>
          )}

          {tab === 'default' && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              {defaultForType ? (
                <>
                  <p className="text-sm text-slate-600">
                    Imagem padrao para <strong>{typeName}</strong>, configuravel pelo Gestor do Sistema.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      setSelected(defaultForType);
                    }}
                    disabled={uploading}
                    data-testid="image-default-select"
                    className={`rounded-xl overflow-hidden border-2 transition-all ${selected === defaultForType ? 'border-blue-600 ring-2 ring-blue-400' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <img src={defaultForType} alt="Imagem padrao" className="max-h-64 object-cover" />
                  </button>
                  {selected === defaultForType && (
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200">Selecionada</Badge>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500 italic py-12">
                  Sem imagem padrao definida para este tipo de objeto.
                  <br />
                  <span className="text-xs">O Gestor do Sistema pode definir uma em Tipos de Objeto.</span>
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3">
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancelar</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleConfirm} disabled={!selected || uploading} data-testid="image-confirm-btn">
            <Check size={14} className="mr-1" /> {uploading ? 'A carregar...' : 'Confirmar imagem'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
